import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { useEVM } from '@/context/EVMContext';
import { Container } from '@/components/Layout/Container';
import { PrimaryHeader } from '@/components/Layout/Header/PrimaryHeader';
import { SecondaryHeader } from '@/components/Layout/Header/SecondaryHeader';

export function EVMTool() {
  const { state, dispatch } = useEVM();
  const { data } = state;

  const handleChange = (field: string, value: any) => {
    dispatch({
      type: 'UPDATE_DATA',
      payload: { [field]: value }
    });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <PrimaryHeader />
      <SecondaryHeader />

      <main className="flex-1">
        <Container>
          <div className="space-y-6 py-8">
            {/* Project Details */}
            <Card>
              <CardHeader>
                <CardTitle>Project Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="projectName">Project Name</Label>
                      <Input
                        id="projectName"
                        value={data.projectName}
                        onChange={(e) => handleChange('projectName', e.target.value)}
                        placeholder="Enter project name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clientName">Client Name</Label>
                      <Input
                        id="clientName"
                        value={data.clientName}
                        onChange={(e) => handleChange('clientName', e.target.value)}
                        placeholder="Enter client name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Project Start Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !data.startDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {data.startDate ? format(data.startDate, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={data.startDate}
                            onSelect={(date) => handleChange('startDate', date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>Project End Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !data.endDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {data.endDate ? format(data.endDate, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={data.endDate}
                            onSelect={(date) => handleChange('endDate', date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div>
                    <Label>Reporting Period</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !data.reportingPeriodStart && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {data.reportingPeriodStart ? format(data.reportingPeriodStart, "PPP") : <span>Start date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={data.reportingPeriodStart}
                            onSelect={(date) => handleChange('reportingPeriodStart', date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>

                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !data.reportingPeriodEnd && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {data.reportingPeriodEnd ? format(data.reportingPeriodEnd, "PPP") : <span>End date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={data.reportingPeriodEnd}
                            onSelect={(date) => handleChange('reportingPeriodEnd', date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Financial Metrics */}
            <Card>
              <CardHeader>
                <CardTitle>Financial Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="bac">Budget at Completion (BAC)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>The total budget allocated for the project. This value remains constant throughout the project.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="bac"
                      type="number"
                      value={data.bac ?? ''}
                      onChange={(e) => handleChange('bac', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Enter BAC"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="ac">Actual Cost (AC)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>The actual cost incurred for the work completed. Enter the total costs to date.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="ac"
                      type="number"
                      value={data.ac ?? ''}
                      onChange={(e) => handleChange('ac', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Enter AC"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="pv">Planned Value (PV)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>The estimated value of work planned to be completed by a specific date. Formula: PV = (Planned % Complete) × BAC.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="pv"
                      type="number"
                      value={data.pv ?? ''}
                      onChange={(e) => handleChange('pv', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Enter PV"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="ev">Earned Value (EV)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>The estimated value of the actual work completed to date. Formula: EV = (Actual % Complete) × BAC.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="ev"
                      type="number"
                      value={data.ev ?? ''}
                      onChange={(e) => handleChange('ev', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Enter EV"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Variances */}
            <Card>
              <CardHeader>
                <CardTitle>Variances</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Schedule Variance (SV)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>The difference between Earned Value (EV) and Planned Value (PV). Formula: SV = EV - PV. A negative value indicates the project is behind schedule.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-2xl font-semibold">
                      {formatCurrency(calculateSV(data))}
                      <TrendIndicator value={calculateSV(data)} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Cost Variance (CV)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>The difference between Earned Value (EV) and Actual Cost (AC). Formula: CV = EV - AC. A negative value indicates the project is over budget.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-2xl font-semibold">
                      {formatCurrency(calculateCV(data))}
                      <TrendIndicator value={calculateCV(data)} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Performance Indices */}
            <Card>
              <CardHeader>
                <CardTitle>Performance Indices</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Schedule Performance Index (SPI)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>A measure of schedule efficiency. Formula: SPI = EV / PV. Values below 1 indicate the project is behind schedule.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-2xl font-semibold">
                      {formatRatio(calculateSPI(data))}
                      <TrendIndicator value={calculateSPI(data) - 1} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Cost Performance Index (CPI)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>A measure of cost efficiency. Formula: CPI = EV / AC. Values below 1 indicate the project is over budget.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-2xl font-semibold">
                      {formatRatio(calculateCPI(data))}
                      <TrendIndicator value={calculateCPI(data) - 1} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>To-Complete Performance Index (TCPI)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>The cost performance required to complete the remaining work within the budget. Formula: TCPI = (BAC - EV) / (BAC - AC). Values above 1 indicate higher cost efficiency is needed.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-2xl font-semibold">
                      {formatRatio(calculateTCPI(data))}
                      <TrendIndicator value={1 - calculateTCPI(data)} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Forecasts */}
            <Card>
              <CardHeader>
                <CardTitle>Forecasts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Estimate at Completion (EAC)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>The projected total cost at completion. Formula: EAC = BAC / CPI.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-2xl font-semibold">
                      {formatCurrency(calculateEAC(data))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Estimate to Complete (ETC)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>The projected cost to complete the remaining work. Formula: ETC = EAC - AC.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-2xl font-semibold">
                      {formatCurrency(calculateETC(data))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </Container>
      </main>
    </div>
  );
}

function TrendIndicator({ value }: { value: number }) {
  if (value === 0) return null;
  return (
    <span className={cn(
      "ml-2 text-sm",
      value > 0 ? "text-green-500" : "text-red-500"
    )}>
      {value > 0 ? "↑" : "↓"}
    </span>
  );
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value || 0);
}

function formatRatio(value: number): string {
  return (value || 0).toFixed(2);
}

// EVM Calculations
function calculateSV(data: any): number {
  return (data.ev || 0) - (data.pv || 0);
}

function calculateCV(data: any): number {
  return (data.ev || 0) - (data.ac || 0);
}

function calculateSPI(data: any): number {
  return data.pv ? (data.ev || 0) / data.pv : 1;
}

function calculateCPI(data: any): number {
  return data.ac ? (data.ev || 0) / data.ac : 1;
}

function calculateTCPI(data: any): number {
  const remaining = (data.bac || 0) - (data.ev || 0);
  const available = (data.bac || 0) - (data.ac || 0);
  return available ? remaining / available : 1;
}

function calculateEAC(data: any): number {
  const cpi = calculateCPI(data);
  return cpi ? (data.bac || 0) / cpi : 0;
}

function calculateETC(data: any): number {
  return Math.max(calculateEAC(data) - (data.ac || 0), 0);
}